/**
 * 詳細画面（Task 13 で本実装）。Task 12 段階ではスタブ。
 *
 * 一覧画面からカードがタップされると呼ばれる。
 * Task 13 でメモ編集・アクションボタン・モーダル起動を追加する。
 */

import { getReservation } from "../api";
import { setRoot, escapeHtml, nl2br, showToast, showError } from "../render";
import { state } from "../store";
import type { Reservation, ReservationStatus } from "../types";
import { renderList } from "./list";
import { openCancelModal } from "./cancelModal";
import { openStatusModal } from "./statusModal";
import { updateMemo } from "../api";

const STATUS_LABELS: Record<ReservationStatus, string> = {
  confirmed: "確定",
  completed: "面談済",
  no_show: "フォロー",
  canceled: "キャンセル",
};

const CONTACT_LABELS: Record<string, string> = {
  zoom: "オンライン面談",
  line_call: "LINE通話",
};

export async function renderDetail(id: string): Promise<void> {
  state.view = "detail";
  setRoot(`<div class="text-center text-gray-500 mt-8">読み込み中...</div>`);

  try {
    const r = await getReservation(id);
    state.selected = r;
    paint(r);
  } catch (e: any) {
    showError(`予約の取得に失敗しました：${e?.message ?? e}`);
    setRoot(`
      <div class="mt-12 text-center text-red-600">予約情報を取得できませんでした。</div>
      <div class="mt-6 text-center">
        <button id="btn-back" class="tap-target px-6 py-3 bg-gray-200 rounded-lg">一覧に戻る</button>
      </div>
    `);
    document.getElementById("btn-back")?.addEventListener("click", () => renderList());
  }
}

function paint(r: Reservation): void {
  const dateLabel = formatDate(r.reservation_date);
  const isConfirmed = r.status === "confirmed";
  const isCanceled = r.status === "canceled";

  setRoot(`
    <div class="mb-3">
      <button
        id="btn-back"
        class="tap-target px-3 py-2 text-gray-700 hover:bg-gray-100 rounded">
        ← 一覧に戻る
      </button>
    </div>

    <div class="bg-white rounded-lg border border-gray-200 p-4 mb-3">
      <div class="flex items-start justify-between gap-3 mb-3">
        <div>
          <div class="text-xs text-gray-500">予約者</div>
          <div class="text-lg font-bold text-gray-900">${escapeHtml(r.customer_name)}</div>
        </div>
        <span class="badge badge-${r.status} flex-shrink-0 mt-1">
          ${escapeHtml(STATUS_LABELS[r.status])}
        </span>
      </div>

      <dl class="text-sm text-gray-800 space-y-2">
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">日時</dt>
          <dd>${escapeHtml(dateLabel)} ${escapeHtml(r.reservation_time)}（${r.duration_minutes}分）</dd>
        </div>
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">連絡方法</dt>
          <dd>${escapeHtml(CONTACT_LABELS[r.contact_method] ?? r.contact_method)}</dd>
        </div>
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">業種</dt>
          <dd>${escapeHtml(r.business_type ?? "—")}</dd>
        </div>
        ${meetUrlHtml(r.meet_url)}
        ${r.cancel_reason ? `
        <div class="flex">
          <dt class="w-24 text-gray-500 flex-shrink-0">キャンセル理由</dt>
          <dd class="text-gray-700">${escapeHtml(r.cancel_reason)}</dd>
        </div>` : ""}
      </dl>
    </div>

    <div class="bg-white rounded-lg border border-gray-200 p-4 mb-3">
      <div class="text-xs text-gray-500 mb-1">相談内容</div>
      <div class="text-sm text-gray-800 leading-relaxed">
        ${r.consultation_topic ? nl2br(r.consultation_topic) : '<span class="text-gray-400">（記入なし）</span>'}
      </div>
    </div>

    <div class="bg-white rounded-lg border border-gray-200 p-4 mb-3">
      <label for="memo" class="block text-sm font-semibold text-gray-700 mb-1">
        オーナーメモ
      </label>
      <p class="text-xs text-gray-500 mb-2">
        社内メモです。お客様には届きません。1000字まで。
      </p>
      <textarea
        id="memo"
        maxlength="1000"
        rows="4"
        class="w-full p-2 border border-gray-300 rounded text-sm"
        placeholder="例：前回相談から3週間経過。次回車検時期に再案内予定。">${escapeHtml(r.owner_memo ?? "")}</textarea>
      <div class="flex justify-end mt-2">
        <button
          id="btn-save-memo"
          class="tap-target px-5 py-2 bg-brand-green text-white rounded-lg text-sm">
          メモを保存
        </button>
      </div>
    </div>

    ${actionButtonsHtml(isConfirmed, isCanceled)}
  `);

  // 戻る
  document.getElementById("btn-back")?.addEventListener("click", () => renderList());

  // メモ保存
  document.getElementById("btn-save-memo")?.addEventListener("click", async () => {
    const ta = document.getElementById("memo") as HTMLTextAreaElement | null;
    if (!ta) return;
    const memo = ta.value;
    if (memo.length > 1000) {
      showToast("メモは1000字以内で入力してください", "warning");
      return;
    }
    const btn = document.getElementById("btn-save-memo") as HTMLButtonElement | null;
    if (btn) { btn.disabled = true; btn.textContent = "保存中..."; }
    try {
      await updateMemo(r.id, memo);
      // 取得済みオブジェクトの memo も同期しておく
      r.owner_memo = memo;
      state.selected = r;
      showToast("メモを保存しました", "success");
    } catch (e: any) {
      showToast(`保存失敗：${e?.message ?? e}`, "error");
    } finally {
      if (btn) { btn.disabled = false; btn.textContent = "メモを保存"; }
    }
  });

  // Meet URL コピー
  document.getElementById("btn-copy-meet")?.addEventListener("click", async () => {
    if (!r.meet_url) return;
    try {
      await navigator.clipboard.writeText(r.meet_url);
      showToast("Meet URL をコピーしました", "success");
    } catch {
      showToast("コピーに失敗しました", "error");
    }
  });

  // ステータス更新
  document.getElementById("btn-completed")?.addEventListener("click", () => {
    openStatusModal(r, "completed", () => renderDetail(r.id));
  });
  document.getElementById("btn-no-show")?.addEventListener("click", () => {
    openStatusModal(r, "no_show", () => renderDetail(r.id));
  });

  // キャンセル
  document.getElementById("btn-cancel")?.addEventListener("click", () => {
    openCancelModal(r, () => renderDetail(r.id));
  });
}

function meetUrlHtml(url: string | null): string {
  if (!url) return "";
  return `
    <div class="flex items-start">
      <dt class="w-24 text-gray-500 flex-shrink-0">Meet URL</dt>
      <dd class="min-w-0 flex-1">
        <a href="${escapeHtml(url)}" target="_blank" rel="noopener noreferrer"
           class="text-blue-600 underline break-all">${escapeHtml(url)}</a>
        <button
          id="btn-copy-meet"
          class="ml-2 tap-target text-xs px-2 py-1 bg-gray-100 rounded border border-gray-300">
          コピー
        </button>
      </dd>
    </div>
  `;
}

function actionButtonsHtml(isConfirmed: boolean, isCanceled: boolean): string {
  if (isCanceled) {
    return `
      <div class="text-center text-sm text-gray-500 mt-4">
        この予約はキャンセル済みです。ステータスは変更できません。
      </div>
    `;
  }
  return `
    <div class="flex flex-col gap-2 mt-2">
      <button
        id="btn-completed"
        ${isConfirmed ? "" : "disabled"}
        class="tap-target px-4 py-3 rounded-lg font-semibold ${
          isConfirmed
            ? "bg-blue-600 text-white"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }">
        面談済にする
      </button>
      <button
        id="btn-no-show"
        ${isConfirmed ? "" : "disabled"}
        class="tap-target px-4 py-3 rounded-lg font-semibold ${
          isConfirmed
            ? "bg-orange-500 text-white"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }">
        フォロー（不参加）
      </button>
      <button
        id="btn-cancel"
        ${isConfirmed ? "" : "disabled"}
        class="tap-target px-4 py-3 rounded-lg font-semibold ${
          isConfirmed
            ? "bg-red-600 text-white"
            : "bg-gray-200 text-gray-400 cursor-not-allowed"
        }">
        キャンセルする
      </button>
    </div>
    ${isConfirmed ? "" : `
      <p class="text-xs text-gray-500 mt-2 text-center">
        確定状態の予約のみ「面談済 / フォロー / キャンセル」に変更できます。
      </p>
    `}
  `;
}

function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const [, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  const w = ["日", "月", "火", "水", "木", "金", "土"][dt.getDay()];
  return `${y}/${Number(mo)}/${Number(d)}(${w})`;
}
